import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Webcam from "react-webcam";
import {
  Button,
  Checkbox,
  Dimmer,
  Dropdown,
  Grid,
  Header,
  Icon,
  Input,
  Loader,
  Message,
  Radio,
  Segment,
  Tab,
} from "semantic-ui-react";

import QRCodeStyling from "qr-code-styling";
import type { CornerDotType, CornerSquareType, DotType, FileExtension } from "qr-code-styling";

import ColorPicker from "react-lib/apps/HISV3/HRM/ColorPicker";

import {
  BODY_SHAPES,
  DEFAULT_QR_STATE,
  EXPORT_SIZE_OPTIONS,
  EYE_BALL_SHAPES,
  EYE_FRAME_SHAPES,
  PREVIEW_SIZE,
  QRDesignState,
  ShapeOption,
  buildQROptions,
  removeLogoBackground,
} from "./GenQRCodeOptions";
import { decodeQRCode } from "./qrReader";

// ------------------------------------------------------------------ styles

const styles = {
  card: {
    height: "100%",
    overflow: "auto",
    padding: "1rem 1.5rem 3rem",
  } as CSSProperties,
  sectionHeader: {
    color: "#5A6579",
    fontSize: "0.95rem",
    letterSpacing: "0.03rem",
    margin: 0,
    textTransform: "uppercase",
  } as CSSProperties,
  fieldRow: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: "0.75rem",
    marginTop: "0.75rem",
  } as CSSProperties,
  fieldLabel: {
    color: "#5A6579",
    fontSize: "0.9rem",
    minWidth: "9rem",
  } as CSSProperties,
  gallery: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginTop: "0.5rem",
  } as CSSProperties,
  dropzone: {
    alignItems: "center",
    border: "1.5px dashed rgba(34, 36, 38, 0.25)",
    borderRadius: "0.4rem",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    marginTop: "0.75rem",
    padding: "1.5rem",
    textAlign: "center",
  } as CSSProperties,
  preview: {
    alignItems: "center",
    background:
      "repeating-conic-gradient(#F1F3F5 0% 25%, #FFFFFF 0% 50%) 50% / 20px 20px",
    border: "1px solid rgba(34, 36, 38, 0.15)",
    borderRadius: "0.4rem",
    display: "flex",
    justifyContent: "center",
    minHeight: `${PREVIEW_SIZE + 24}px`,
    padding: "0.75rem",
  } as CSSProperties,
};

const shapeTileStyle = (selected: boolean): CSSProperties => ({
  alignItems: "center",
  background: "white",
  border: `2px solid ${selected ? "#2185D0" : "rgba(34, 36, 38, 0.15)"}`,
  borderRadius: "0.4rem",
  color: "#1B1C1D",
  cursor: "pointer",
  display: "flex",
  height: "3.25rem",
  justifyContent: "center",
  width: "3.25rem",
});

// -------------------------------------------------------------- sub views

type ShapeGalleryProps<T extends string> = {
  options: ShapeOption<T>[];
  value: T;
  onChange: (value: T) => void;
};

const ShapeGallery = <T extends string>(props: ShapeGalleryProps<T>): JSX.Element => (
  <div style={styles.gallery}>
    {props.options.map((option) => (
      <div
        key={option.value}
        style={shapeTileStyle(option.value === props.value)}
        title={option.label}
        onClick={() => {
          props.onChange(option.value);
        }}
      >
        {option.icon}
      </div>
    ))}
  </div>
);

const Section = (props: { title: string; children: React.ReactNode }): JSX.Element => (
  <Segment>
    <div style={styles.sectionHeader}>{props.title}</div>
    {props.children}
  </Segment>
);

// ------------------------------------------------------------------- card

const EXPORT_FORMAT_OPTIONS = [
  { key: "png", text: "PNG", value: "png" },
  { key: "svg", text: "SVG", value: "svg" },
  { key: "jpeg", text: "JPEG", value: "jpeg" },
];

const QR_READER_SCAN_INTERVAL = 300;

const QRCodeReader = (): JSX.Element => {
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scanTimerRef = useRef<number | null>(null);
  const [result, setResult] = useState<string>("");
  const [error, setError] = useState<string>("");

  const handleResult = useCallback((value: string): void => {
    if (value) {
      setResult(value);
      setError("");
    }
  }, []);

  const scanCamera = useCallback((): void => {
    const video = webcamRef.current?.video;

    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext("2d");

    if (!context || !canvas.width || !canvas.height) {
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const decodedValue = decodeQRCode(imageData.data, imageData.width, imageData.height);

    if (decodedValue) {
      handleResult(decodedValue);
    }
  }, [handleResult]);

  useEffect(() => {
    scanTimerRef.current = window.setInterval(scanCamera, QR_READER_SCAN_INTERVAL);

    return () => {
      if (scanTimerRef.current) {
        window.clearInterval(scanTimerRef.current);
      }
    };
  }, [scanCamera]);

  const handleImageFile = useCallback(
    (file?: File): void => {
      if (!file) {
        return;
      }

      if (!file.type.startsWith("image/")) {
        setError("กรุณาเลือกไฟล์รูปภาพที่มี QR Code");

        return;
      }

      const reader = new FileReader();

      reader.addEventListener("load", () => {
        const image = new window.Image();

        image.addEventListener("load", () => {
          const canvas = document.createElement("canvas");
          canvas.width = image.naturalWidth;
          canvas.height = image.naturalHeight;

          const context = canvas.getContext("2d");

          if (!context) {
            setError("ไม่สามารถอ่านไฟล์รูปภาพได้");

            return;
          }

          context.drawImage(image, 0, 0);
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          const decodedValue = decodeQRCode(imageData.data, imageData.width, imageData.height);

          if (decodedValue) {
            handleResult(decodedValue);
          } else {
            setError("ไม่พบ QR Code ในรูปภาพ");
          }
        });

        image.addEventListener("error", () => {
          setError("ไม่สามารถอ่านไฟล์รูปภาพได้");
        });
        image.src = String(reader.result ?? "");
      });

      reader.readAsDataURL(file);
    },
    [handleResult]
  );

  return (
    <Section title="QR Reader">
      <div style={{ color: "#5A6579", marginTop: "0.75rem" }}>
        เปิดกล้องเพื่อสแกน QR Code หรือเลือกไฟล์รูปภาพ
      </div>

      <div
        style={{
          background: "#F1F3F5",
          borderRadius: "0.4rem",
          marginTop: "0.75rem",
          overflow: "hidden",
        }}
      >
        <Webcam
          audio={false}
          mirrored={false}
          ref={webcamRef}
          screenshotFormat="image/png"
          style={{ display: "block", height: "auto", maxHeight: "28rem", width: "100%" }}
          videoConstraints={{ facingMode: { ideal: "environment" } }}
          onUserMediaError={() => {
            setError("ไม่สามารถเปิดกล้องได้ กรุณาอนุญาตการใช้งานกล้องหรือเลือกไฟล์รูปภาพ");
          }}
        />
      </div>

      <input
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        type="file"
        onChange={(event) => {
          handleImageFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />

      <Button
        basic
        icon="upload"
        content="อ่านจากไฟล์รูปภาพ"
        style={{ marginTop: "0.75rem" }}
        onClick={() => {
          fileInputRef.current?.click();
        }}
      />

      {!!error && <Message error content={error} style={{ marginTop: "0.75rem" }} />}

      {result ? (
        <Message positive style={{ marginTop: "0.75rem" }}>
          <Message.Header>ข้อมูลจาก QR Code</Message.Header>
          <Input fluid readOnly style={{ marginTop: "0.75rem" }} value={result} />
        </Message>
      ) : (
        <Message info content="ยังไม่มีข้อมูลจาก QR Code" style={{ marginTop: "0.75rem" }} />
      )}
    </Section>
  );
};

const CardGenQRCode = (): JSX.Element => {
  const [state, setState] = useState<QRDesignState>(DEFAULT_QR_STATE);
  const [contentMode, setContentMode] = useState<"qrcodeId" | "url">("qrcodeId");
  const [qrcodeId, setQrcodeId] = useState<string>("");
  const [url, setUrl] = useState<string>("");
  const [logoOriginal, setLogoOriginal] = useState<string | null>(null);
  const [transparentLogo, setTransparentLogo] = useState<boolean>(false);
  const [processingLogo, setProcessingLogo] = useState<boolean>(false);
  const [exportFormat, setExportFormat] = useState<FileExtension>("png");
  const [error, setError] = useState<string>("");
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const previewRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasData = !!state.data.trim();

  const contentValue = contentMode === "qrcodeId" ? qrcodeId : url;
  const contentPlaceholder = contentMode === "qrcodeId" ? "ID" : "https://...";

  const patch = useCallback((changes: Partial<QRDesignState>): void => {
    setState((current) => ({ ...current, ...changes }));
  }, []);

  // Preview: the instance is recreated on every change because update() deep
  // merges, which cannot clear an option that was removed (gradient / logo).
  useEffect(() => {
    const timer = setTimeout(() => {
      const container = previewRef.current;

      if (!container) {
        return;
      }

      container.innerHTML = "";

      if (!hasData) {
        return;
      }

      try {
        new QRCodeStyling(buildQROptions(state, PREVIEW_SIZE)).append(container);
        setError("");
      } catch {
        // qrcode-generator throws when the content is too long for the QR spec.
        setError("ข้อความยาวเกินไปสำหรับ QR Code กรุณาลดความยาวของข้อมูล");
      }
    }, 150);

    return () => {
      clearTimeout(timer);
    };
  }, [state, hasData]);

  const applyLogo = useCallback(
    async (dataUrl: string | null, transparent: boolean): Promise<void> => {
      if (!dataUrl || !transparent) {
        patch({ logo: dataUrl });

        return;
      }

      setProcessingLogo(true);

      try {
        patch({ logo: await removeLogoBackground(dataUrl) });
      } catch {
        setError("ไม่สามารถลบพื้นหลังของโลโก้ได้");
        patch({ logo: dataUrl });
      }

      setProcessingLogo(false);
    },
    [patch]
  );

  const handleSelectFile = useCallback(
    (file?: File | null): void => {
      if (!file) {
        return;
      }

      if (!file.type.startsWith("image/")) {
        setError("กรุณาเลือกไฟล์รูปภาพ");

        return;
      }

      setError("");

      const reader = new FileReader();

      reader.addEventListener("load", () => {
        const dataUrl = reader.result as string;

        setLogoOriginal(dataUrl);
        patch({ logoName: file.name });

        void applyLogo(dataUrl, transparentLogo);
      });

      reader.readAsDataURL(file);
    },
    [applyLogo, patch, transparentLogo]
  );

  const handleRemoveLogo = useCallback((): void => {
    setLogoOriginal(null);
    patch({ logo: null, logoName: "" });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [patch]);

  const handleDownload = useCallback((): void => {
    if (!hasData) {
      return;
    }

    setError("");

    try {
      void new QRCodeStyling(buildQROptions(state, state.exportSize))
        .download({ extension: exportFormat, name: "qr-code" })
        .catch(() => {
          setError("ไม่สามารถดาวน์โหลดไฟล์ได้");
        });
    } catch {
      setError("ไม่สามารถสร้าง QR Code ได้ กรุณาตรวจสอบข้อมูลที่กรอก");
    }
  }, [exportFormat, hasData, state]);

  const gradientDisabled = state.colorMode !== "gradient";

  const exportSizeOptions = useMemo(
    () =>
      EXPORT_SIZE_OPTIONS.map((size) => ({
        key: size,
        text: `${size} x ${size} px`,
        value: size,
      })),
    []
  );

  return (
    <div style={styles.card}>
      <Header as="h3" style={{ marginBottom: "1rem" }}>
        <Icon name="qrcode" />
        <Header.Content>
          GenQRcode
          <Header.Subheader>สร้าง QR Code พร้อมปรับแต่งสี โลโก้ และรูปแบบ</Header.Subheader>
        </Header.Content>
      </Header>

      {!!error && (
        <Message
          error
          content={error}
          onDismiss={() => {
            setError("");
          }}
        />
      )}

      <Tab
        menu={{ secondary: true, pointing: true }}
        panes={[
          {
            menuItem: { key: "generator", content: "Generate QR Code", icon: "qrcode" },
            render: () => (
              <Tab.Pane attached={false}>
                <Grid stackable>
        <Grid.Column width={10}>
          <Section title="Enter Content">
            <div style={styles.fieldRow}>
              <Radio
                checked={contentMode === "qrcodeId"}
                label="ID"
                name="contentMode"
                onChange={() => {
                  setContentMode("qrcodeId");
                  patch({
                    data: qrcodeId ? `https://line.ishealth.app/${qrcodeId}` : "",
                  });
                }}
              />
              <Radio
                checked={contentMode === "url"}
                label="Entire URL"
                name="contentMode"
                onChange={() => {
                  setContentMode("url");
                  patch({ data: url });
                }}
              />
            </div>
            <Input
              fluid
              placeholder={contentPlaceholder}
              style={{ marginTop: "0.75rem", width: "100%" }}
              type="text"
              value={contentValue}
              onChange={(_event, data) => {
                const value = String(data.value ?? "");

                if (contentMode === "qrcodeId") {
                  setQrcodeId(value);
                  patch({ data: value ? `https://line.ishealth.app/${value}` : "" });
                } else {
                  setUrl(value);
                  patch({ data: value });
                }
              }}
            />
            {!hasData && (
              <div style={{ color: "#9F3A38", fontSize: "0.85rem", marginTop: "0.35rem" }}>
                กรุณากรอก ID หรือ URL
              </div>
            )}
          </Section>

          <Section title="Set Colors">
            <div style={styles.fieldRow}>
              <Radio
                checked={state.colorMode === "single"}
                label="Single Color"
                name="colorMode"
                onChange={() => {
                  patch({ colorMode: "single" });
                }}
              />
              <Radio
                checked={state.colorMode === "gradient"}
                label="Color Gradient"
                name="colorMode"
                onChange={() => {
                  patch({ colorMode: "gradient" });
                }}
              />
            </div>

            {state.colorMode === "single" ? (
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>Foreground Color</div>
                <ColorPicker
                  value={state.fgColor}
                  onChange={(color: string) => {
                    patch({ fgColor: color });
                  }}
                />
              </div>
            ) : (
              <>
                <div style={styles.fieldRow}>
                  <div style={styles.fieldLabel}>Gradient Type</div>
                  <Dropdown
                    selection
                    options={[
                      { key: "linear", text: "Linear", value: "linear" },
                      { key: "radial", text: "Radial", value: "radial" },
                    ]}
                    value={state.gradient.type}
                    onChange={(_event, data) => {
                      patch({
                        gradient: { ...state.gradient, type: data.value as "linear" | "radial" },
                      });
                    }}
                  />
                </div>
                <div style={styles.fieldRow}>
                  <div style={styles.fieldLabel}>Gradient Colors</div>
                  <ColorPicker
                    value={state.gradient.start}
                    onChange={(color: string) => {
                      patch({ gradient: { ...state.gradient, start: color } });
                    }}
                  />
                  <Button
                    basic
                    icon="exchange"
                    title="สลับสี"
                    onClick={() => {
                      patch({
                        gradient: {
                          ...state.gradient,
                          end: state.gradient.start,
                          start: state.gradient.end,
                        },
                      });
                    }}
                  />
                  <ColorPicker
                    value={state.gradient.end}
                    onChange={(color: string) => {
                      patch({ gradient: { ...state.gradient, end: color } });
                    }}
                  />
                </div>
                <div style={styles.fieldRow}>
                  <div style={styles.fieldLabel}>Rotation</div>
                  <input
                    disabled={gradientDisabled || state.gradient.type === "radial"}
                    max={360}
                    min={0}
                    step={5}
                    style={{ flex: 1, minWidth: "12rem" }}
                    type="range"
                    value={state.gradient.rotationDeg}
                    onChange={(event) => {
                      patch({
                        gradient: { ...state.gradient, rotationDeg: Number(event.target.value) },
                      });
                    }}
                  />
                  <div style={{ minWidth: "3rem" }}>{state.gradient.rotationDeg}&deg;</div>
                </div>
              </>
            )}

            <div style={styles.fieldRow}>
              <div style={styles.fieldLabel}>Background Color</div>
              <ColorPicker
                value={state.bgColor}
                onChange={(color: string) => {
                  patch({ bgColor: color, bgTransparent: false });
                }}
              />
              <Checkbox
                checked={state.bgTransparent}
                label="Transparent Background"
                onChange={(_event, data) => {
                  patch({ bgTransparent: !!data.checked });
                }}
              />
            </div>

            <div style={styles.fieldRow}>
              <Checkbox
                checked={state.customEyeColor}
                disabled={state.colorMode === "gradient"}
                label="Custom Eye Color"
                onChange={(_event, data) => {
                  patch({ customEyeColor: !!data.checked });
                }}
              />
            </div>

            {state.customEyeColor && state.colorMode === "single" && (
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>Eye Frame / Eye Ball</div>
                <ColorPicker
                  value={state.eyeFrameColor}
                  onChange={(color: string) => {
                    patch({ eyeFrameColor: color });
                  }}
                />
                <ColorPicker
                  value={state.eyeBallColor}
                  onChange={(color: string) => {
                    patch({ eyeBallColor: color });
                  }}
                />
              </div>
            )}
          </Section>

          <Section title="Add Logo Image">
            <input
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              type="file"
              onChange={(event) => {
                handleSelectFile(event.target.files?.[0]);
              }}
            />

            {state.logo ? (
              <div style={styles.fieldRow}>
                <img
                  alt="logo"
                  src={state.logo}
                  style={{
                    background:
                      "repeating-conic-gradient(#F1F3F5 0% 25%, #FFFFFF 0% 50%) 50% / 10px 10px",
                    border: "1px solid rgba(34, 36, 38, 0.15)",
                    borderRadius: "0.3rem",
                    height: "4rem",
                    objectFit: "contain",
                    width: "4rem",
                  }}
                />
                <div style={{ color: "#5A6579" }}>{state.logoName}</div>
                <Button basic icon="trash alternate" title="ลบโลโก้" onClick={handleRemoveLogo} />
              </div>
            ) : (
              <div
                style={{
                  ...styles.dropzone,
                  background: isDragging ? "#F4F8FF" : undefined,
                  borderColor: isDragging ? "#2185D0" : "rgba(34, 36, 38, 0.25)",
                }}
                onClick={() => {
                  fileInputRef.current?.click();
                }}
                onDragLeave={() => {
                  setIsDragging(false);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragging(true);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setIsDragging(false);
                  handleSelectFile(event.dataTransfer.files?.[0]);
                }}
              >
                <Icon color="grey" name="cloud upload" size="large" />
                <div style={{ color: "#5A6579", marginTop: "0.5rem" }}>
                  ลากรูปมาวาง หรือคลิกเพื่อเลือกไฟล์
                </div>
              </div>
            )}

            <div style={styles.fieldRow}>
              <Checkbox
                checked={state.hideBackgroundDots}
                label="Remove Background Behind Logo"
                onChange={(_event, data) => {
                  patch({ hideBackgroundDots: !!data.checked });
                }}
              />
            </div>

            <div style={styles.fieldRow}>
              <Checkbox
                checked={transparentLogo}
                disabled={!logoOriginal}
                label="Remove Logo Image Background (transparent)"
                onChange={(_event, data) => {
                  const checked = !!data.checked;

                  setTransparentLogo(checked);
                  void applyLogo(logoOriginal, checked);
                }}
              />
              {processingLogo && <Loader active inline size="tiny" />}
            </div>

            <div style={styles.fieldRow}>
              <div style={styles.fieldLabel}>Logo Size</div>
              <input
                disabled={!state.logo}
                max={0.5}
                min={0.15}
                step={0.01}
                style={{ flex: 1, minWidth: "12rem" }}
                type="range"
                value={state.logoSize}
                onChange={(event) => {
                  patch({ logoSize: Number(event.target.value) });
                }}
              />
              <div style={{ minWidth: "3rem" }}>{Math.round(state.logoSize * 100)}%</div>
            </div>
          </Section>

          <Section title="Customize Design">
            <div style={{ color: "#5A6579", marginTop: "0.75rem" }}>Body Shape</div>
            <ShapeGallery<DotType>
              options={BODY_SHAPES}
              value={state.bodyShape}
              onChange={(value) => {
                patch({ bodyShape: value });
              }}
            />

            <div style={{ color: "#5A6579", marginTop: "1rem" }}>Eye Frame Shape</div>
            <ShapeGallery<CornerSquareType>
              options={EYE_FRAME_SHAPES}
              value={state.eyeFrameShape}
              onChange={(value) => {
                patch({ eyeFrameShape: value });
              }}
            />

            <div style={{ color: "#5A6579", marginTop: "1rem" }}>Eye Ball Shape</div>
            <ShapeGallery<CornerDotType>
              options={EYE_BALL_SHAPES}
              value={state.eyeBallShape}
              onChange={(value) => {
                patch({ eyeBallShape: value });
              }}
            />
          </Section>
        </Grid.Column>

        <Grid.Column width={6}>
          <div style={{ position: "sticky", top: "1rem" }}>
            <Segment>
              <div style={styles.sectionHeader}>Preview</div>
              <Dimmer.Dimmable dimmed={processingLogo} style={{ marginTop: "0.75rem" }}>
                <Dimmer inverted active={processingLogo}>
                  <Loader size="small" />
                </Dimmer>
                <div style={styles.preview}>
                  <div ref={previewRef} />
                </div>
              </Dimmer.Dimmable>

              <div style={styles.fieldRow}>
                <div style={{ ...styles.fieldLabel, minWidth: "4rem" }}>Size</div>
                <Dropdown
                  selection
                  options={exportSizeOptions}
                  style={{ flex: 1 }}
                  value={state.exportSize}
                  onChange={(_event, data) => {
                    patch({ exportSize: Number(data.value) });
                  }}
                />
              </div>

              <div style={styles.fieldRow}>
                <div style={{ ...styles.fieldLabel, minWidth: "4rem" }}>Format</div>
                <Dropdown
                  selection
                  options={EXPORT_FORMAT_OPTIONS}
                  style={{ flex: 1 }}
                  value={exportFormat}
                  onChange={(_event, data) => {
                    setExportFormat(data.value as FileExtension);
                  }}
                />
              </div>

              <Button
                fluid
                primary
                content="Download"
                disabled={!hasData}
                icon="download"
                style={{ marginTop: "1rem" }}
                onClick={handleDownload}
              />

              <Button
                basic
                fluid
                content="Reset"
                style={{ marginTop: "0.5rem" }}
                onClick={() => {
                  setState(DEFAULT_QR_STATE);
                  setContentMode("qrcodeId");
                  setQrcodeId("");
                  setUrl("");
                  setLogoOriginal(null);
                  setTransparentLogo(false);

                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
              />
            </Segment>
          </div>
        </Grid.Column>
                </Grid>
              </Tab.Pane>
            ),
          },
          {
            menuItem: { key: "reader", content: "QR Reader", icon: "camera" },
            render: () => (
              <Tab.Pane attached={false}>
                <QRCodeReader />
              </Tab.Pane>
            ),
          },
        ]}
      />
    </div>
  );
};

CardGenQRCode.displayName = "CardGenQRCode";

export default React.memo(CardGenQRCode);
